import { API_HOST, AUTHENTICATE_TOKEN_KEY } from 'app/config'
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { jwtDecode } from 'jwt-decode'

import { authHTApi } from '../auth'

const apiClient = axios.create({
  baseURL: API_HOST,
  headers: {},
})
export type AuthToken = {
  token_type: string
  idToken: string
  accessToken: string
  refreshToken: string
}

export const setHeader = (authToken: AuthToken) => {
  if (!authToken) {
    return
  }
  apiClient.defaults.headers.common.Authorization = `${authToken.token_type} ${authToken.idToken}`
}

export function onResponse(response: AxiosResponse): AxiosResponse {
  return response.data
}

const refreshTokenIfNeed = async (): Promise<AuthToken | null> => {
  const storedAuthToken = JSON.parse(
    window.localStorage.getItem(AUTHENTICATE_TOKEN_KEY) || 'null',
  ) as AuthToken
  if (!storedAuthToken) {
    return null
  }
  const idToken = jwtDecode(storedAuthToken.idToken)
  const tokenExp = (idToken.exp || 0) * 1000 - Date.now()
  // If the token is valid for more than 5 minutes, or expired for more than 3 hours, do not refresh
  if (tokenExp > 5 * 60 * 1000 || tokenExp < -3 * 60 * 60 * 1000) {
    return storedAuthToken
  }

  const payload = {
    userPoolName: 'clinic-admin',
    userName: idToken.sub,
    refreshToken: storedAuthToken.refreshToken,
  }
  try {
    const result = await authHTApi.getUserAccessToken(payload)
    const { idToken: newIdToken, accessToken: newAccessToken } = result.data
    const newAuthToken: AuthToken = {
      token_type: 'Bearer',
      idToken: newIdToken,
      accessToken: newAccessToken,
      refreshToken: storedAuthToken.refreshToken,
    }
    setHeader(newAuthToken)
    window.localStorage.setItem(
      AUTHENTICATE_TOKEN_KEY,
      JSON.stringify(newAuthToken),
    )
    return newAuthToken
  } catch (error: any) {
    console.error('error refresh token', error)
  }
  return null
}

async function onRequest(config: InternalAxiosRequestConfig) {
  const accessToken = await refreshTokenIfNeed()
  if (accessToken) {
    const authorizationToken = `Bearer ${accessToken?.idToken}`
    config.headers.Authorization = authorizationToken
  }
  return config
}

const reqInterceptor = apiClient.interceptors.request.use(onRequest)
apiClient.interceptors.response.use(onResponse)

/**
 * NOTE: Storybook/Vitest実行時用
 *       jwtトークンを使用したonRequestを無効化する
 */
function initForTest() {
  apiClient.interceptors.request.eject(reqInterceptor)
}

if (window.localStorage.getItem(AUTHENTICATE_TOKEN_KEY)) {
  const authToken = JSON.parse(
    window.localStorage.getItem(AUTHENTICATE_TOKEN_KEY) || 'null',
  ) as AuthToken
  setHeader(authToken)
}

export { apiClient, initForTest }
